import { Location } from '@angular/common';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, OnInit } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { Producer, User, UsersRepository } from '@homeleite/domain';
import { catchError, filter, map, mergeMap, Observable } from 'rxjs';
import { ProducersApiRepository } from '../../../../../../../libs/domain/src/lib/producers/producers-api.repository';
import { Actives, Filter, FilterOp } from '@homeleite/arch';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';

@Component({
    selector: 'homeleite-producer-details',
    templateUrl: './producer-details.component.html',
    styleUrls: ['./producer-details.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class ProducerDetailsComponent implements OnInit {
    associatedUsers$: Observable<User[]>;
    form: FormGroup;
    producer: Producer;
    selectedTab = 0;

    constructor(
        private activatedRoute: ActivatedRoute,
        private fb: FormBuilder,
        private location: Location,
        private repository: ProducersApiRepository,
        private usersRepository: UsersRepository,
        private cd: ChangeDetectorRef
    ) {}

    ngOnInit(): void {
        this.form = this.fb.group({
            name: ['', Validators.required],
            code: [''],
            email: ['', Validators.email]
        });

        this.activatedRoute.queryParams.subscribe((params) => {
            if (params['tab'] !== undefined) {
                this.selectedTab = Number(params['tab']);
                // deep-link ?tab=2 → carrega usuários
                queueMicrotask(() => this.onTabSelected(this.selectedTab));
            }
        });

        this.activatedRoute.params
            .pipe(
                map((p) => p['id']),
                filter((id) => Boolean(id)),
                mergeMap((id) => this.repository.find(id)),
                catchError((e) => {
                    alert('Registro não encontrado');
                    this.location.back();
                    throw e;
                })
            )
            .subscribe((producer: Producer) => {
                if (producer) {
                    this.producer = producer;
                    this.form.patchValue(producer);
                    this.onTabSelected(this.selectedTab);
                    this.cd.markForCheck();
                }
            });
    }

    goBack(): void {
        this.location.back();
    }

    get producerInitials(): string {
        const name = (this.producer?.name || this.producer?.code || '?').trim();
        const parts = name.split(/\s+/).filter(Boolean);
        if (parts.length >= 2) {
            return (parts[0][0] + parts[1][0]).toUpperCase();
        }
        return name.slice(0, 2).toUpperCase();
    }

    get contactLines(): { label: string; value: string }[] {
        const list = this.producer?.contacts || [];
        return list
            .map((c) => ({
                label: this.contactLabel(c.category),
                value: (c.detail || '').trim()
            }))
            .filter((c) => c.value);
    }

    get addressLines(): string[] {
        const list = this.producer?.addresses || [];
        return list
            .map((address) => {
                const parts = [
                    address?.street,
                    address?.number,
                    address?.district,
                    address?.city?.name,
                    address?.city?.state?.acronym
                ]
                    .map((p) => (p == null ? '' : String(p).trim()))
                    .filter((p) => p && p !== '-');
                return parts.join(', ');
            })
            .filter(Boolean);
    }

    private contactLabel(category: string | null | undefined): string {
        const c = (category || '').toLowerCase();
        if (c.includes('mail') || c.includes('email')) return 'E-mail';
        if (c.includes('phone') || c.includes('fone') || c.includes('celular')) return 'Telefone';
        return category || 'Contato';
    }

    onTabSelected(index: number): void {
        // 0 = Usuários Associados (dados cadastrais foram para o cabeçalho)
        if (index === 0 && this.producer) {
            this.loadAssociatedUsers();
        }
    }

    loadAssociatedUsers() {
        const producerFilter = new Filter('producer.$id', FilterOp.EQ, this.producer.id);

        this.associatedUsers$ = this.usersRepository.list('', [producerFilter], [], Actives.TRUE);
    }
}
